import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react'
import NavModal from '@component/header/GlobalNavigation/MobileNavigation/NavModal/NavModal'
import WizButton from '@component/buttons/WizButton'
import {useTranslation} from '@lib/intl/useTranslations'
import {ILoginInfoData, useLoginStore} from '@store/LoginStore'
import {shallow} from 'zustand/shallow'
import MobileNavigationSubItem from '@component/header/GlobalNavigation/MobileNavigation/MobileNavigationSubItem/MobileNavigationSubItem'
import useLogin from '@hook/useLogin'
import useNavigation from '@hook/useNavigation'
import UserThumbnail from '@component/images/UserThumbnail'
import Text from '@component/Text/Text'
import {apiEditThumbnail} from '@api/user/account/editThumbnail'
import useSnackbar from '@hook/useSnackbar'
import {apiUploadImage} from '@api/core/image/uploadImage'
import {useLoginModalStore} from '@store/LoginModalStore'
import {useRouter} from 'next/router'
import ProTag from '@component/tags/ProTag'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'
import PendingInvoiceModal from '@feature/common/components/PendingInvoiceModal/PendingInvoiceModal'
import NotRegisteredApiModal from '@feature/portfolio/components/NotRegisteredApiModal/NotRegisteredApiModal'
import IconPlus from '@svg/common/ic_plus.svg'
import WizIcon from '@component/images/WizIcon'
import IconPlusDark from '@svg/common/ic_plus_dark.svg'
import useAccounts from '@hook/useAccounts'
import {createLog} from '@util/logs'
import IconNotificationMobile from '@svg/gnb/ic_mo_alarm_on.svg'
import NotificationCount from '@component/header/GlobalNavigation/NotificationDropdown/NotificationCount'
import useQueryGetPoint from '@hook/query/account/point/useQueryGetPoint'
import useFormatNumber from '@hook/useFormatNumber'
import {
    getLinkUrl,
    URL_ACCOUNT_API,
    URL_ACCOUNT_INVOICE,
    URL_ACCOUNT_LANDING_PRO,
    URL_ACCOUNT_NOTIFICATION,
    URL_ACCOUNT_POINT,
    URL_ACCOUNT_PRO,
    URL_ACCOUNT_REFERRAL,
    URL_ACCOUNT_SETTING,
    URL_ACCOUNT_SUBSCRIBER,
    URL_ACCOUNT_WALLET,
    URL_PORTFOLIO,
} from '@constant/url'
import {toast} from 'react-toastify'
import WizToastMessage from '@component/snackbar/WizToastMessage'
import SharePortfolioOptionModal from '@feature/portfolio/components/SharePortfolioOptionModal/SharePortfolioOptionModal'
import useCopy from '@hook/useCopy'
import IconClose from '@svg/gnb/ic_mo_close.svg'
import WizThumbnailChangeInput from '@component/inputs/WizThumbnailChangeInput'
import IconShare from '@svg/common/ic_btn_share_v2.svg'
import Link from 'next/link'
import IconNoticeRed from '@svg/common/ic_notice_red.svg'
import TagText from '@component/Text/TagText'
import IconNext from '@svg/common/ic_btn_next.svg'
import IconAccountPortfolio from '@svg/gnb/ic_account_portfolio.svg'
import {useImportantAlertsContext} from '@hook/useImportantAlerts'
import IconLeaguePointWhite from '@svg/common/ic_league_point_symbol_white.svg'
import IconLeaguePoint from '@svg/common/ic_league_point_symbol.svg'
import AccountImportantAlertModal from '@feature/account/components/importantAlert/AccountImportantAlertModal'

interface IProps {
    onClickNotification?: () => void
}

const MobileAccountNavigation = ({onClickNotification}: IProps, ref: any) => {
    const t = useTranslation()
    const {id, login, logout} = useLoginStore(
        state => ({
            id: state.id,
            login: state.login,
            logout: state.logout,
        }),
        shallow,
    )
    const {alertList, invoiceAlertList, settingAlertList, proAlertList} = useImportantAlertsContext()
    const list = useMemo(() => {
        return [...invoiceAlertList, ...alertList, ...proAlertList, ...settingAlertList]
    }, [alertList, invoiceAlertList, settingAlertList, proAlertList])

    const {isMe} = useLogin(id)

    const {accounts, removeStorage: removeAccountsStorage} = useAccounts()
    const {showSnackbar, showErrorSnackbar} = useSnackbar()
    const {data: me, refetch: refreshMe} = useQueryFetchMe(undefined)
    const {data: point} = useQueryGetPoint()

    const modalRef = useRef<any>()
    const imageFileRef = useRef<any>()
    const importantAlertModalRef = useRef<any>()
    const sharePortfolioOptionModalRef = useRef<any>()
    const notRegisteredApiModalRef = useRef<any>()
    const showLoginModal = useLoginModalStore(state => state.showLoginModal)

    const router = useRouter()
    const {displayNumber} = useFormatNumber()
    const {copyToClipboard} = useCopy()
    const {goLeaderboard} = useNavigation()

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef?.current?.show()
        },
        close: () => {
            modalRef?.current?.close()
        },
    }))
    const closeModal = () => {
        modalRef?.current?.close()
    }

    const changeImage = async e => {
        const file = e?.target?.files[0]

        if (file) {
            const result = await apiUploadImage(file)
            if (result.success) {
                const {data, error} = await apiEditThumbnail(result.image_url)
                if (data) {
                    showSnackbar(t('mobileAccountNavigation.thumbnail.changeImage.success'))
                    refreshMe()
                    return
                } else {
                    showErrorSnackbar(error)
                }
            }
        }

        e.target.value = ''
        showSnackbar(t('mobileAccountNavigation.thumbnail.changeImage.fail'))
        return
    }

    const resetDefaultImage = useCallback(async () => {
        const {data, error} = await apiEditThumbnail('')
        if (data) {
            showSnackbar(t('mobileAccountNavigation.thumbnail.changeImage.success'))
            refreshMe()
        } else {
            showErrorSnackbar(error)
        }
    }, [showSnackbar, showErrorSnackbar, refreshMe, t])

    const onPickImage = () => {
        imageFileRef && imageFileRef.current.click()
    }

    const imageMenuOptionList = useMemo(
        () => [
            {title: t('mobileAccountNavigation.thumbnail.menu.pickImage'), onClick: onPickImage},
            {title: t('mobileAccountNavigation.thumbnail.menu.setDefault'), onClick: resetDefaultImage},
        ],
        [t, resetDefaultImage],
    )

    const isCheckOverLoginTime = (lastLoginTime: number) => {
        if (new Date().getTime() > lastLoginTime) {
            return true
        } else {
            return false
        }
    }

    const onClickChangeAccount = user => {
        createLog('event', 'navigation_mobile_change_account', {user_id: user?.id})
        localStorage.removeItem('isCheckInvoiceModal')
        if (isCheckOverLoginTime(user?.login_end_at)) {
            showLoginModal()
        } else {
            const userName = router?.locale === 'ko' && user?.nickname ? user?.nickname : user?.name
            login(user?.id, user?.access_token)
            showSnackbar(t('mobileAccountNavigation.multiLogin.changeAccount.text', {username: userName}))
        }
        closeModal()
    }

    const onClickSignOut = async () => {
        createLog('event', 'navigation_mobile_logout')
        const getUserAccounts = JSON.parse(localStorage.getItem('user_accounts'))
        if (getUserAccounts && getUserAccounts?.length > 1) {
            await logout()

            goLeaderboard()
            closeModal()
        } else {
            logout()
            goLeaderboard()
            closeModal()
        }
        localStorage.removeItem('isCheckInvoiceModal')
    }

    const onClickSignOutAll = () => {
        createLog('event', 'navigation_mobile_logout_all')
        removeAccountsStorage()
        localStorage.removeItem('isCheckInvoiceModal')
        logout()
        closeModal()
    }

    const pendingInvoiceModalRef = useRef<any>()
    useEffect(() => {
        const isCheckPendingInvoice = localStorage.getItem('isCheckInvoiceModal')
        if (!isCheckPendingInvoice && me?.pending_invoices > 0 && id === me?.id) {
            localStorage.setItem('isCheckInvoiceModal', 'true')
            pendingInvoiceModalRef?.current?.show()
        }
    }, [id, me])

    const onClickSharePortfolio = () => {
        if (!me?.keys || me?.keys?.length < 1) {
            notRegisteredApiModalRef?.current?.show()
        } else {
            if (isMe) {
                createLog('event', 'key_portfolio_sidebar_open_share_portfolio_option_modal')
                sharePortfolioOptionModalRef?.current?.show()
            } else {
                createLog('event', 'key_portfolio_sidebar_share_portfolio_copy_link')
                copyToClipboard(`https://leagueoftraders.io/ko/user/${id}/portfolio`)
                toast(<WizToastMessage text={t('common.copied')} />)
            }
        }
    }

    const userLocalName = useMemo(() => {
        return me?.nicknames?.find(item => item.language === router?.locale)?.nickname ?? me?.name
    }, [me, router?.locale])

    const openFilePicker = () => {
        const fileInput = document.getElementById('thumb-file-input')
        if (fileInput) {
            setTimeout(() => fileInput?.click(), 0)
        }
    }

    return (
        <>
            <NavModal
                ref={modalRef}
                className={'bg-white dark:bg-bg_dark_popup max-w-[270px]'}
                headerColorClassName={'bg-gray09 dark:bg-bg_dark_white02 py-[18px] !hidden'}>
                <div className={'flex flex-col min-h-screen'}>
                    <div className={' bg-gray09 dark:bg-bg_dark_white02'}>
                        <div className={'py-[18px] px-[20px] flex items-center justify-between'}>
                            <WizIcon
                                className={`p-0 relative hover:bg-transparent01 dark:hover:bg-dark_transparent01 md:hover:bg-transparent fill-gray01 dark:fill-dark_gray01 cursor-pointer`}
                                onClick={() => {
                                    onClickNotification()
                                    closeModal()
                                    createLog('event', 'navigation_click_notification_icon')
                                }}>
                                <>
                                    <div className={'relative flex'}>
                                        <IconNotificationMobile width={24} className={`block`} />
                                    </div>
                                    <NotificationCount className={'!bottom-[-2px]'} />
                                </>
                            </WizIcon>
                            <WizIcon
                                className={'cursor-pointer'}
                                onClick={() => {
                                    closeModal()
                                }}>
                                <IconClose className={'w-[24px] fill-gray01 dark:fill-dark_gray01'} />
                            </WizIcon>
                        </div>
                        <div className={'pb-[20px] px-[20px] '}>
                            <div className={'flex items-center gap-x-[10px] mb-[15px]'}>
                                <div className={'relative'}>
                                    <UserThumbnail
                                        src={me?.thumbnail}
                                        size={50}
                                        isPro={me?.is_pro}
                                        containerClassName={'w-[50px] flex-none'}
                                    />
                                    <WizThumbnailChangeInput
                                        downViewAlign={'left'}
                                        onCustomChangeImage={openFilePicker}
                                    />
                                </div>
                                <div className={'grow'}>
                                    <Text className={'text-ti2 text-gray01 dark:text-dark_gray01 break-all'}>
                                        {userLocalName}
                                        {me?.is_pro && <ProTag size={'small'} className={'ml-[5px]'} type={'text'} />}
                                    </Text>
                                    <div
                                        onClick={onClickSharePortfolio}
                                        className={'flex items-center mt-[3px] gap-x-[3px] cursor-pointer '}>
                                        <IconShare className={'w-[12px] fill-gray02 dark:fill-dark_gray02'} />
                                        <Text className={'underline text-body3 text-gray02 dark:text-dark_gray02'}>
                                            {t('portfolio.sidebar.share.portfolio.short')}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                            {list?.length > 0 ? (
                                <div
                                    onClick={e => {
                                        createLog('event', 'mobile_account_navigation_alert_click')
                                        if (list?.length === 1) {
                                            modalRef?.current.close()
                                            list?.at(0)?.action()
                                            e?.preventDefault()
                                            e?.stopPropagation()
                                        } else {
                                            modalRef?.current.close()
                                            importantAlertModalRef?.current?.show()
                                            e?.preventDefault()
                                            e?.stopPropagation()
                                        }
                                    }}
                                    className={
                                        'cursor-pointer mb-[10px] items-center flex gap-x-[5px] p-[10px] rounded-[5px] ring-[1px] ring-red_tint01 dark:ring-dark_red_tint01 bg-red_tint02 dark:bg-dark_red_tint02'
                                    }>
                                    <IconNoticeRed
                                        className={'flex-none fill-red_shade dark:fill-dark_red_shade w-[14px]'}
                                    />
                                    <TagText className={'text-body3 text-gray01 dark:text-dark_gray01 grow'}>
                                        {t('accountAlert.counts', {
                                            count: list?.length,
                                        })}
                                    </TagText>
                                    <IconNext className={'w-[12px] fill-gray02 dark:fill-dark_gray02 flex-none'} />
                                </div>
                            ) : null}
                            <Link href={getLinkUrl(URL_PORTFOLIO(me?.id))} className={'max-w-[180px] mx-auto block'}>
                                <button
                                    onClick={() => {
                                        createLog('event', 'navigation_mobile_click_menu', {menu: 'portfolio'})
                                        closeModal()
                                    }}
                                    className={`w-full bg-white dark:bg-bg_dark_white03 py-[10px] gap-x-[5px] ring-[1px] ring-gray06 dark:ring-dark_gray06 rounded-[20px] mx-auto justify-center flex items-center hover:bg-gray09 dark:hover:bg-dark_gray09 active:bg-gray07 dark:active:bg-dark_gray07`}>
                                    <IconAccountPortfolio className={'w-[12px] fill-gray01 dark:fill-dark_gray01'} />
                                    <Text
                                        enablePreWhiteSpace={false}
                                        className={'text-body3 whitespace-nowrap text-gray01 dark:text-dark_gray01'}>
                                        {t('mobileAccountNavigation.myPortfolio')}
                                    </Text>
                                </button>
                            </Link>
                        </div>
                    </div>

                    <div
                        className={
                            'border-b border-b-gray08 dark:border-b-dark_gray08 bg-white dark:bg-bg_dark_popup py-[5px]'
                        }>
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.setting')}
                            link={getLinkUrl(URL_ACCOUNT_SETTING)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'setting'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                            renderRight={settingAlertList?.length > 0 ? settingAlertList?.length : undefined}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.api')}
                            link={getLinkUrl(URL_ACCOUNT_API)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'api'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                            renderRight={alertList?.length > 0 ? alertList?.length : undefined}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.wallet')}
                            link={getLinkUrl(URL_ACCOUNT_WALLET)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'wallet'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.invoice')}
                            link={getLinkUrl(URL_ACCOUNT_INVOICE)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'invoice'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                            renderRight={invoiceAlertList?.length > 0 ? invoiceAlertList?.length : undefined}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.followers')}
                            link={getLinkUrl(URL_ACCOUNT_SUBSCRIBER)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'subscriber'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.notifications')}
                            link={getLinkUrl(URL_ACCOUNT_NOTIFICATION)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'notification'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                        />
                        <MobileNavigationSubItem
                            text={t('mobileAccountNavigation.user.referral')}
                            link={getLinkUrl(URL_ACCOUNT_REFERRAL)}
                            onClick={() => {
                                createLog('event', 'navigation_mobile_click_menu', {menu: 'referral'})
                                closeModal()
                            }}
                            className={'py-[7px]'}
                            textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                        />
                        <div className={'flex items-center justify-between'}>
                            <MobileNavigationSubItem
                                link={getLinkUrl(URL_ACCOUNT_PRO)}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_click_menu', {menu: 'pro'})
                                    closeModal()
                                }}
                                text={t('mobileAccountNavigation.user.pro')}
                                textClassName={'text-ti2 bg-gradient_pro !text-transparent bg-clip-text '}
                                className={'flex-1 py-[7px]'}
                                renderRight={proAlertList?.length > 0 ? proAlertList?.length : undefined}
                            />
                            <MobileNavigationSubItem
                                link={getLinkUrl(URL_ACCOUNT_LANDING_PRO)}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_click_menu', {menu: 'pro_landing'})
                                    closeModal()
                                }}
                                text={t('mobileAccountNavigation.user.pro.more')}
                                textClassName={'text-body_comm underline text-gray01 dark:text-dark_gray01'}
                                className={'py-[7px]'}
                            />
                        </div>
                        <div className={'flex items-center justify-between'}>
                            <MobileNavigationSubItem
                                link={getLinkUrl(URL_ACCOUNT_POINT)}
                                text={t('mobileAccountNavigation.user.point')}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_click_menu', {menu: 'point'})
                                    closeModal()
                                }}
                                className={'py-[7px]'}
                                textClassName={'text-body2 text-gray02 dark:text-dark_gray02'}
                                leftIcon={
                                    <>
                                        <IconLeaguePointWhite
                                            className={'w-[16px] h-[16px] hidden dark:block flex-none'}
                                        />
                                        <IconLeaguePoint
                                            className={'w-[16px] h-[16px] fill-black dark:hidden flex-none'}
                                        />
                                    </>
                                }
                            />

                            <MobileNavigationSubItem
                                text={t('point.text', {point: displayNumber(point?.current_point || 0)})}
                                link={getLinkUrl(URL_ACCOUNT_POINT)}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_click_menu', {menu: 'point'})
                                    closeModal()
                                }}
                                className={'py-[7px]'}
                                textClassName={'text-body_comm underline text-gray01 dark:text-dark_gray01'}
                            />
                        </div>
                    </div>
                    <div
                        className={
                            'py-[5px] bg-white dark:bg-bg_dark_popup border-b border-b-gray08 dark:border-b-dark_gray08'
                        }>
                        <Text className={'px-[30px] text-ti4 py-[5px] text-gray01 dark:text-dark_gray01'}>
                            {t('mobileAccountNavigation.multiLogin.title')}
                        </Text>
                        {accounts?.map((user: ILoginInfoData) => {
                            if (user?.id != id) {
                                return (
                                    <div
                                        key={user?.id}
                                        onClick={() => onClickChangeAccount(user)}
                                        className={
                                            'px-[30px] flex items-center gap-x-[10px] py-[7px] cursor-pointer hover:bg-gray07 dark:hover:bg-dark_gray07'
                                        }>
                                        <UserThumbnail src={user?.thumbnail} size={20} isPro={user?.isPro ?? false} />
                                        <div className={'flex-1 max-w-[100px]'}>
                                            <Text
                                                enablePreWhiteSpace={false}
                                                className={
                                                    'text-body2 text-gray02 dark:text-dark_gray02 truncate whitespace-nowrap'
                                                }>
                                                {router?.locale === 'ko' ? user?.nickname || user?.name : user?.name}
                                            </Text>
                                        </div>
                                        {isCheckOverLoginTime(user?.login_end_at) && (
                                            <Text className={'text-body3 text-gray02 dark:text-dark_gray02'}>
                                                {t('mobileAccountNavigation.multiLogin.expire')}
                                            </Text>
                                        )}
                                        {user?.isPro && <ProTag size={'small'} />}
                                    </div>
                                )
                            }
                        })}

                        <div
                            onClick={() => {
                                createLog('event', 'navigation_mobile_add_account')
                                showLoginModal()
                            }}
                            className={'px-[30px] flex items-center gap-x-[10px] py-[7px] cursor-pointer'}>
                            <IconPlusDark className={'hidden dark:block'} />
                            <IconPlus className={'dark:hidden'} />
                            <Text className={'text-body2 text-gray02 dark:text-dark_gray02 flex-1'}>
                                {t('globalNavigation.multiLogin.addAccount')}
                            </Text>
                        </div>
                    </div>

                    <div className={'p-[20px] pb-[30px] flex flex-col gap-y-[10px] items-center'}>
                        <WizButton
                            onClick={() => onClickSignOut()}
                            text={t('globalNavigation.my.menu.logout')}
                            buttonType={'border'}
                            className={'w-full !px-0'}
                        />
                        <WizButton
                            onClick={() => onClickSignOutAll()}
                            text={t('globalNavigation.multiLogin.logout.all')}
                            buttonType={'border'}
                            className={'w-full !px-0'}
                        />
                    </div>
                </div>
                <SharePortfolioOptionModal ref={sharePortfolioOptionModalRef} />
                <NotRegisteredApiModal ref={notRegisteredApiModalRef} />
            </NavModal>
            <AccountImportantAlertModal ref={importantAlertModalRef} alertList={list} />
            <PendingInvoiceModal ref={pendingInvoiceModalRef} id={id} />
        </>
    )
}

export default React.forwardRef(MobileAccountNavigation)
